import { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
  Chip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { divisionService } from '../services'
import ManagementSearchBar from '../components/shared/ManagementSearchBar'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'

const DivisionManagementPage = () => {
  const [divisions, setDivisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedDivision, setSelectedDivision] = useState(null)
  const [editFormData, setEditFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const applySearchQuery = useCallback((q) => {
    setDebouncedSearch(q)
    setPage(0)
  }, [])

  const fetchDivisions = async () => {
    try {
      setLoading(true)
      const params = { page: page + 1, limit: rowsPerPage }
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
      const data = await divisionService.getDivisions(params)
      if (data?.items) {
        setDivisions(data.items)
        setTotalItems(data.pagination?.total || 0)
      } else {
        setDivisions(data || [])
        setTotalItems(data?.length || 0)
      }
    } catch (error) {
      console.error('Error fetching divisions:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDivisions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch])

  const handleEditClick = (division) => {
    setSelectedDivision(division)
    setEditFormData({
      name: division.name || '',
      slug: division.slug || '',
    })
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedDivision(null)
    setEditFormData({})
    setActionError(null)
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)
      const payload = { ...editFormData }
      await divisionService.updateDivision(selectedDivision.id, payload)
      setSuccessMessage('Division updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      handleEditClose()
      fetchDivisions()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update division')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true)
      await divisionService.deleteDivision(selectedDivision.id)
      setSuccessMessage('Division deleted successfully')
      setDeleteConfirmOpen(false)
      setSelectedDivision(null)
      fetchDivisions()
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete division')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTreeIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Manage Divisions
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchDivisions}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card>
        <CardContent>
          <ManagementSearchBar
            label="Search by Name or Slug"
            placeholder="Search divisions..."
            onApplySearchQuery={applySearchQuery}
          />
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={3} />
                ) : divisions.length === 0 ? (
                  <TableEmptyState colSpan={3} message="No divisions found" />
                ) : (
                  divisions.map((division) => (
                    <TableRow key={division.id} hover>
                      <TableCell>{division.name}</TableCell>
                      <TableCell>
                        <Chip label={division.slug} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditClick(division)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedDivision(division)
                            setDeleteConfirmOpen(true)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Division</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Slug"
              value={editFormData.slug || ''}
              onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })}
              fullWidth
              size="small"
              helperText="Use UPPER_SNAKE_CASE (e.g. LOOSE_COMMODITY)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Division</DialogTitle>
        <DialogContent>
          <Typography>
            Delete division &quot;{selectedDivision?.name}&quot;? Products must be reassigned first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  )
}

export default DivisionManagementPage
